import "./apex25.css";

import { type ComponentType, type ReactNode, useEffect } from "react";
import { type RouteObject, useLocation } from "react-router-dom";

import { movedToApex25Url } from "@/lib/apex25AppUrl";
import { LegalIndustryPage } from "@/pages/marketing/industries/LegalIndustryPage";

import { Apex25Footer, Apex25Header } from "./Apex25Chrome";
import { APEX25_INDUSTRY_PAGES } from "./industries";

// Components for the Apex 25 industry pages listed in industries.ts.
const APEX25_INDUSTRY_COMPONENTS: Record<string, ComponentType> = {
  "/for-legal": LegalIndustryPage,
};

/**
 * Apex 25 header and footer around an industry page. The page keeps its own styles, so only the
 * header and footer sit inside `.apex25` (whose element rules would otherwise restyle its links).
 */
const Apex25IndustryLayout = ({ children }: { children: ReactNode }) => (
  <>
    <div className="apex25 apex25-chrome">
      <Apex25Header onHome={false} />
    </div>
    {children}
    <div className="apex25 apex25-chrome">
      <Apex25Footer onHome={false} />
    </div>
  </>
);

/**
 * Pages that moved to tryapex25.com. On production recruit.theresume.io this sends in-app
 * navigation there (the server already 301s full loads); on staging and localhost it renders.
 */
export const MovedToApex25 = ({ children }: { children: ReactNode }) => {
  const { pathname, search, hash } = useLocation();
  const target = movedToApex25Url(pathname);

  useEffect(() => {
    if (target) window.location.replace(`${target}${search}${hash}`);
  }, [target, search, hash]);

  return target ? null : <>{children}</>;
};

/** Route objects for every Apex 25 industry page, used by both routers. */
export const apex25IndustryRoutes = (): RouteObject[] =>
  Object.keys(APEX25_INDUSTRY_PAGES).map((path) => {
    const Page = APEX25_INDUSTRY_COMPONENTS[path];
    // Fail loudly: without a component the page would bounce to recruit.theresume.io.
    if (!Page) throw new Error(`No component registered for Apex 25 page ${path}`);
    return {
      path,
      element: (
        <MovedToApex25>
          <Apex25IndustryLayout>
            <Page />
          </Apex25IndustryLayout>
        </MovedToApex25>
      ),
    };
  });
